/**
 * The user's own data — backup and restore.
 *
 * Everything in IndexedDB falls into one of two families: the **content cache**,
 * which the API can always hand back (chapters, topics, phrases, audio,
 * thumbnails), and the **user's own rows**, which nothing can — favorites, the
 * dictionary, and the topics they finished. Only the second family is worth a
 * backup, and this module is the single place that knows which stores those are.
 *
 * That is why all three are id-shaped rows with no binaries: they serialize to
 * plain JSON exactly as they sit in the store, so a backup is a small text file and
 * a restore is a `put` per row — no images to re-encode, nothing to reconcile
 * against the catalogue. A row pointing at content that isn't cached (or no longer
 * exists) is harmless: every list already skips ids it can't resolve, so a backup
 * can be restored onto a cold cache and light up as the content comes down.
 *
 * The same document is what travels to the account's server-side backup
 * (`POST /api/v1/study/backup`): on sign-out the app pushes it up and then **clears
 * these three stores**, and after sign-in it pulls them back — so the data lives on
 * the account rather than on the device, survives a reinstall, and never leaks into
 * the next account signed in here. See the bottom half of this file. Nothing else moves it; there is no periodic autosave and no
 * "export to a file" screen (both can be added on top without touching the
 * stores).
 */

import { fetchStudyBackup, saveStudyBackup } from '../auth/api.js';
import { STORES, clear, getAll, put } from '../db/idb.js';

/**
 * Backup payload version. Bump it when a store's row shape changes in a way an
 * older file can't express — {@link importUserData} refuses what it doesn't know.
 */
export const BACKUP_FORMAT = 1;

/** A timestamp field: kept when sane, stamped now when a file hasn't got one. */
function timestamp(value) {
  return Number.isFinite(value) ? value : Date.now();
}

/**
 * The user's stores and the exact row each one accepts.
 *
 * Doubles as the import filter: a restored row is rebuilt field by field through
 * these, so a hand-edited (or malicious) file can't smuggle extra properties into
 * a store — and doubles as the documentation of what a user row actually is.
 */
const USER_ROW = {
  [STORES.favorites]: (row) => ({ id: row.id }),
  [STORES.dictionary]: (row) => ({ id: row.id, addedAt: timestamp(row.addedAt) }),
  [STORES.topicsDone]: (row) => ({ id: row.id, doneAt: timestamp(row.doneAt) }),
};

/** The stores a backup covers — favorites, dictionary, completed topics. */
export const USER_STORES = Object.keys(USER_ROW);

/**
 * Everything the user made, as a plain JSON-serializable object.
 *
 * @returns {Promise<{format: number, exportedAt: string, data: Record<string, Array<object>>}>}
 */
export async function exportUserData() {
  const data = {};
  for (const store of USER_STORES) {
    data[store] = await getAll(store);
  }
  return { format: BACKUP_FORMAT, exportedAt: new Date().toISOString(), data };
}

/**
 * Put a backup back.
 *
 * Restores each known store from the payload and **replaces** what is there by
 * default — that is what "przywróć kopię zapasową" means; pass `replace: false` to
 * merge a file into the current data instead (rows with the same id win, the rest
 * stay). A store the file doesn't carry is left alone either way, so a partial
 * backup can't wipe anything it never mentioned.
 *
 * Rows are rebuilt through {@link USER_ROW}, and anything without a usable string
 * id is dropped rather than stored.
 *
 * @param {object|string} backup The object from {@link exportUserData}, or its JSON text.
 * @param {{replace?: boolean}} [options]
 * @returns {Promise<Record<string, number>>} Rows written, per store.
 * @throws {Error} On unreadable JSON or a format this build doesn't know (Polish
 *   message — it is meant to be shown).
 */
export async function importUserData(backup, { replace = true } = {}) {
  let payload = backup;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      throw new Error('Nieprawidłowy plik kopii zapasowej.');
    }
  }
  if (!payload || typeof payload !== 'object' || typeof payload.data !== 'object') {
    throw new Error('Nieprawidłowy plik kopii zapasowej.');
  }
  if (payload.format !== BACKUP_FORMAT) {
    throw new Error(`Nieobsługiwana wersja kopii zapasowej (${payload.format}).`);
  }

  const written = {};
  for (const store of USER_STORES) {
    const rows = payload.data[store];
    if (!Array.isArray(rows)) continue; // store absent from this file → leave it as it is

    if (replace) await clear(store);
    const valid = rows.filter((row) => row && typeof row.id === 'string' && row.id);
    await Promise.all(valid.map((row) => put(store, USER_ROW[store](row))));
    written[store] = valid.length;
  }
  return written;
}

/* -------------------------------------------------------------------------- */
/* The account's server-side copy                                             */
/* -------------------------------------------------------------------------- */

/** Does this backup carry nothing the user made? */
function isEmptyBackup(backup) {
  return USER_STORES.every((store) => !backup.data[store]?.length);
}

/**
 * Push the device's user data up as the account's backup.
 *
 * **Skips the request when there is nothing to back up** and says so with `false`.
 * That is not an optimisation, it is the guard against the obvious way to lose
 * everything: sign in on a fresh device, don't restore, sign out — and an empty
 * document would have replaced a good backup (the endpoint keeps one row per
 * account and overwrites it). The cost of the guard is that deliberately clearing
 * everything doesn't propagate; the next non-empty push corrects the copy.
 *
 * Called from the sign-out path through {@link offloadUserData}, *before* the
 * session is cleared — the token is what names the account, so a backup is
 * impossible afterwards. Deliberately not called when an account is deleted (that
 * is an erase, not a save) and impossible on a **forced** sign-out
 * (`handleExpiredSession` runs precisely because the token is gone), where the data
 * simply stays on the device until the next real sign-out.
 *
 * The one caller outside the session's path is the **required-update gate**
 * (`views/home.js`): a `hardUpdate` may end with the app being reinstalled, so the
 * data is committed to the account while there is still a session to do it with.
 * That path pushes and stops there — it does **not** go through
 * {@link offloadUserData}, because the user stays signed in on a build they can't
 * use, and the rows are the only thing waiting for them if the update stalls.
 *
 * @param {string} jwt Access token.
 * @returns {Promise<boolean>} `true` when a document was sent, `false` when there
 *   was nothing to send. Failures come out as `ApiError` — the caller decides
 *   whether they matter (sign-out treats them as not fatal).
 */
export async function pushUserData(jwt) {
  const backup = await exportUserData();
  if (isEmptyBackup(backup)) return false;
  await saveStudyBackup(jwt, backup);
  return true;
}

/**
 * Wipe the user's three stores, leaving the content cache untouched.
 *
 * Only ever correct **after** the data is safe somewhere else — see
 * {@link offloadUserData}, the one caller. Never call it on its own.
 */
export function clearUserData() {
  return Promise.all(USER_STORES.map((store) => clear(store))).then(() => undefined);
}

/**
 * The sign-out handover: push the device's user data up to the account, then take
 * it off the device.
 *
 * The order is the whole point and is why this lives here rather than in the
 * sign-out handler: **nothing is cleared until the push has resolved.** A failed
 * upload (offline, 413, server down) throws before the wipe, so the rows stay
 * exactly where they are — they are the only copy in that case, and the next
 * sign-out tries again. Sequenced, not concurrent, for the same reason.
 *
 * Leaving the device clean is what makes the next sign-in honest: the stores are
 * filled from the account by {@link pullUserData}, so somebody signing into a
 * *different* account on this device can't inherit the previous one's stars, saved
 * phrases and completions. The content cache (chapters, topics, audio, images) is
 * deliberately **not** touched — none of it is tied to an account, and re-signing-in
 * should not mean re-downloading every clip.
 *
 * An empty device pushes nothing (see {@link pushUserData}) and the wipe is then a
 * no-op, which is the same outcome by a shorter road.
 *
 * @param {string} jwt Access token.
 * @returns {Promise<boolean>} What the push reported: `true` when a document was
 *   stored, `false` when there was nothing to store. Throws if the push failed —
 *   the device keeps its data, and the caller (`components/shell.js`) still signs
 *   the user out.
 */
export async function offloadUserData(jwt) {
  const pushed = await pushUserData(jwt);
  await clearUserData();
  return pushed;
}

/**
 * Pull the account's backup down and **merge** it into the local stores.
 *
 * Merge, not replace (`replace: false`): whatever this device already holds is the
 * user's too — they may have starred a topic before ever signing out — so a restore
 * adds to it rather than taking its place. For an id in both, the server's row wins
 * (it is written last), which only ever moves an `addedAt` / `doneAt`.
 *
 * Called right after a successful sign-in (`views/verify.js`), the one moment we
 * know the local stores may be behind the account. Restoring onto a cold content
 * cache is fine — see the note at the top of this file.
 *
 * @param {string} jwt Access token.
 * @returns {Promise<?Record<string, number>>} Rows written per store, or `null`
 *   when the account has no backup stored (a fresh account, or one that never
 *   signed out).
 */
export async function pullUserData(jwt) {
  const payload = await fetchStudyBackup(jwt);
  if (!payload) return null;
  return importUserData(payload, { replace: false });
}
